import api from "src/apis/api"
import { buildImageFormData, hasImageChanges } from "src/utils/imageFormData"

// ── Projects ───────────────────────────────────────────────────────────────
export async function fetchProjects({ page = 1, per_page = 20, status = "", project_type = "", active = "", q = "" } = {}) {
  const params = new URLSearchParams()
  params.set("page", page)
  params.set("per_page", per_page)
  if (status) params.set("status", status)
  if (project_type) params.set("project_type", project_type)
  if (active !== "" && active !== null && active !== undefined) params.set("active", active)
  if (q) params.set("q", q)

  const { data } = await api.get(`/api/v1/projects?${params}`)
  return data
}

// One-click assign (or unassign with membershipId = null) a project.
export async function assignProject(id, membershipId) {
  const { data } = await api.patch(`/api/v1/projects/${id}/assign`, {
    assigned_membership_id: membershipId,
  })
  return data
}

// Org members for the assignee picker.
export async function fetchMembers({ q = "" } = {}) {
  const params = new URLSearchParams()
  params.set("per_page", 100)
  if (q) params.set("q", q)
  const { data } = await api.get(`/api/v1/memberships?${params}`)
  return data
}

export async function fetchProject(id) {
  const { data } = await api.get(`/api/v1/projects/${id}`)
  return data
}

export async function createProject(payload, images = {}) {
  if (hasImageChanges(images)) {
    const form = buildImageFormData("project", payload, {
      ...images, fileKey: "images", removeKey: "remove_image_ids",
    })
    const { data } = await api.post("/api/v1/projects", form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    return data
  }
  const { data } = await api.post("/api/v1/projects", { project: payload })
  return data
}

export async function updateProject(id, payload, images = {}) {
  if (hasImageChanges(images)) {
    const form = buildImageFormData("project", payload, {
      ...images, fileKey: "images", removeKey: "remove_image_ids",
    })
    const { data } = await api.patch(`/api/v1/projects/${id}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    return data
  }
  const { data } = await api.patch(`/api/v1/projects/${id}`, { project: payload })
  return data
}

export async function deleteProject(id) {
  const { data } = await api.delete(`/api/v1/projects/${id}`)
  return data
}

// ── Towers ─────────────────────────────────────────────────────────────────
export async function fetchTowers(projectId) {
  const { data } = await api.get(`/api/v1/projects/${projectId}/towers`)
  return data
}

export async function createTower(projectId, payload) {
  const { data } = await api.post(`/api/v1/projects/${projectId}/towers`, { tower: payload })
  return data
}

export async function updateTower(projectId, id, payload) {
  const { data } = await api.patch(`/api/v1/projects/${projectId}/towers/${id}`, { tower: payload })
  return data
}

// ── Units ──────────────────────────────────────────────────────────────────
export async function fetchUnits(projectId, { tower_id = "", status = "" } = {}) {
  const params = new URLSearchParams()
  if (tower_id) params.set("tower_id", tower_id)
  if (status) params.set("status", status)

  const qs = params.toString()
  const { data } = await api.get(`/api/v1/projects/${projectId}/units${qs ? `?${qs}` : ""}`)
  return data
}

export async function createUnit(projectId, payload) {
  const { data } = await api.post(`/api/v1/projects/${projectId}/units`, { unit: payload })
  return data
}

export async function updateUnit(projectId, id, payload) {
  const { data } = await api.patch(`/api/v1/projects/${projectId}/units/${id}`, { unit: payload })
  return data
}

export async function deleteUnit(projectId, id) {
  const { data } = await api.delete(`/api/v1/projects/${projectId}/units/${id}`)
  return data
}

// Block / unblock / transition a unit's status, with an audited reason.
export async function changeUnitStatus(projectId, id, { status, reason } = {}) {
  const { data } = await api.post(
    `/api/v1/projects/${projectId}/units/${id}/status`,
    { status, reason }
  )
  return data
}

// ── Bulk import ──────────────────────────────────────────────────────────────
// Upload an .xlsx of units. `partial` imports valid rows and reports the rest.
export async function importUnits(projectId, file, { partial = false } = {}) {
  const form = new FormData()
  form.append("file", file)
  if (partial) form.append("partial", "true")

  const { data } = await api.post(`/api/v1/projects/${projectId}/units/import`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  })
  return data
}

// Download the units import template (.xlsx).
export async function downloadUnitTemplate(projectId) {
  const response = await api.get(`/api/v1/projects/${projectId}/units/import_template`, {
    responseType: "blob",
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement("a")
  link.href = url
  link.download = "clozdl-units-template.xlsx"
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
